/**
 * EcosystemCalendarPage — `/admin/calendar`
 *
 * Everything dated across FWM in one place: services, events, exams, school
 * timetables, meetings and facility servicing. Colour-coded by category with
 * independent filter toggles, scoped to the branch of the hierarchy the
 * signed-in admin actually administers.
 */
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api, { getErrorMessage } from '@/lib/api';
import { isoDay, addDays as addLocalDays, parseIsoDay } from '@/lib/localDate';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ServiceTimesManager } from '@/components/church/ServiceTimesManager';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  ArrowLeft, CalendarDays, ChevronLeft, ChevronRight, Plus, Repeat, Globe, Lock,
  Trash2, Pencil, X, Clock, MapPin, ExternalLink, AlertTriangle,
} from 'lucide-react';

const TONE = {
  indigo: { chip: 'bg-indigo-50 text-indigo-700 border-indigo-200', dot: 'bg-indigo-500' },
  violet: { chip: 'bg-violet-50 text-violet-700 border-violet-200', dot: 'bg-violet-500' },
  sky: { chip: 'bg-sky-50 text-sky-700 border-sky-200', dot: 'bg-sky-500' },
  rose: { chip: 'bg-rose-50 text-rose-700 border-rose-200', dot: 'bg-rose-500' },
  amber: { chip: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-500' },
  orange: { chip: 'bg-orange-50 text-orange-700 border-orange-200', dot: 'bg-orange-500' },
  teal: { chip: 'bg-teal-50 text-teal-700 border-teal-200', dot: 'bg-teal-500' },
  slate: { chip: 'bg-slate-100 text-slate-600 border-slate-200', dot: 'bg-slate-400' },
};
const CATEGORY_TONE = { service: 'indigo', event: 'violet', school_timetable: 'sky',
  exam: 'rose', meeting: 'amber', maintenance: 'orange', rehearsal: 'teal', other: 'slate' };
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
// A calendar day is a LOCAL day — see lib/localDate.js. The previous `iso()`
// here went through `toISOString()`, which converts to UTC first, so in Harare
// (UTC+2) every key in the grid came back one day early. Because 365 days is
// 52 weeks plus one day, a grid shifted back by one day draws each date on the
// weekday it falls on in the NEXT year: October 2026 rendered as October 2027.
// That is the "calendar is ahead by a year" report, and it also pushed the
// 31st of every month off the end of the grid.
const iso = isoDay;
const addDays = addLocalDays;
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
  'August', 'September', 'October', 'November', 'December'];

const EcosystemCalendarPage = () => {
  const qc = useQueryClient();
  const [view, setView] = useState('month');
  const [anchor, setAnchor] = useState(new Date());
  const [hidden, setHidden] = useState([]);
  const [scopeId, setScopeId] = useState('');
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [patternsFor, setPatternsFor] = useState('');

  const window_ = useMemo(() => {
    if (view === 'week') {
      const start = addDays(anchor, -((anchor.getDay() + 6) % 7));
      return { start: iso(start), end: iso(addDays(start, 6)) };
    }
    if (view === 'agenda') return { start: iso(anchor), end: iso(addDays(anchor, 60)) };
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const last = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
    return { start: iso(first), end: iso(last) };
  }, [view, anchor]);

  const { data: scopes } = useQuery({
    queryKey: ['calendar-scopes'],
    queryFn: async () => (await api.get('/calendar/scopes')).data,
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['calendar-feed', window_.start, window_.end, scopeId],
    queryFn: async () => (await api.get('/calendar/feed',
      { params: { start: window_.start, end: window_.end, scope_id: scopeId } })).data,
    retry: false,
  });

  const del = useMutation({
    mutationFn: (id) => api.delete(`/calendar/entries/${id}`),
    onSuccess: () => {
      toast.success('Entry removed');
      setSelected(null);
      qc.invalidateQueries({ queryKey: ['calendar-feed'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const categories = data?.categories || [];
  const tone = (key) => TONE[(categories.find((c) => c.key === key) || {}).colour || 'slate'];
  const visible = (data?.items || []).filter((i) => !hidden.includes(i.category));
  const byDay = useMemo(() => {
    const map = {};
    visible.forEach((i) => { (map[i.date] = map[i.date] || []).push(i); });
    return map;
  }, [visible]);

  const step = (delta) => {
    if (view === 'week') setAnchor(addDays(anchor, delta * 7));
    else if (view === 'agenda') setAnchor(addDays(anchor, delta * 30));
    else setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + delta, 1));
  };

  const monthCells = useMemo(() => {
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const lead = (first.getDay() + 6) % 7;
    const total = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0).getDate();
    const cells = Array(lead).fill(null);
    for (let d = 1; d <= total; d += 1) {
      cells.push(iso(new Date(anchor.getFullYear(), anchor.getMonth(), d)));
    }
    while (cells.length % 7) cells.push(null);
    return cells;
  }, [anchor]);

  const weekCells = useMemo(() => {
    const start = addDays(anchor, -((anchor.getDay() + 6) % 7));
    return Array.from({ length: 7 }, (_, i) => iso(addDays(start, i)));
  }, [anchor]);

  const todayIso = iso(new Date());
  const label = view === 'month'
    ? `${MONTHS[anchor.getMonth()]} ${anchor.getFullYear()}`
    : `${window_.start} → ${window_.end}`;

  const Chip = ({ item }) => (
    <button onClick={() => setSelected(item)} data-testid={`calendar-item-${item.id}`}
            className={`w-full truncate rounded border px-1.5 py-0.5 text-left text-[10px] ${tone(item.category).chip}`}>
      <span className={`mr-1 inline-block h-1.5 w-1.5 rounded-full align-middle ${tone(item.category).dot}`} />
      {item.time && <span className="mr-1 font-semibold tabular-nums">{item.time}</span>}
      {item.title}
      {/* A conference shows which day of the run this cell is. */}
      {item.span_days > 1 && (
        <span className="ml-1 opacity-60">· {item.span_index}/{item.span_days}</span>
      )}
      {/* iter 635 — a repeating occurrence is marked, so it can be told apart
          from an event somebody created for that date on its own. */}
      {item.recurring && (
        <Repeat className="ml-1 inline h-2.5 w-2.5 align-middle opacity-70"
                data-testid={`calendar-repeat-${item.id}`} />
      )}
    </button>
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="ecosystem-calendar-page">
      <header className="border-b bg-white">
        <div className="mx-auto max-w-7xl px-4 py-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Link to="/admin" className="text-slate-400 hover:text-slate-700" data-testid="calendar-back">
                <ArrowLeft className="h-5 w-5" />
              </Link>
              <div>
                <h1 className="flex items-center gap-2 text-xl font-bold text-slate-900">
                  <CalendarDays className="h-5 w-5 text-indigo-600" /> Ecosystem calendar
                </h1>
                <p className="text-xs text-slate-500" data-testid="calendar-scope-label">
                  {data?.scope?.label || '…'} · {visible.length} entr{visible.length === 1 ? 'y' : 'ies'} in view
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <select value={scopeId} onChange={(e) => setScopeId(e.target.value)}
                      data-testid="calendar-scope-select"
                      className="h-8 max-w-[220px] rounded-md border border-slate-200 bg-white px-2 text-xs">
                <option value="">Everything I can see</option>
                {(scopes?.items || []).map((o) => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
              <div className="flex overflow-hidden rounded-md border border-slate-200 bg-white">
                {['month', 'week', 'agenda'].map((v) => (
                  <button key={v} onClick={() => setView(v)} data-testid={`calendar-view-${v}`}
                          className={`px-2.5 py-1.5 text-[11px] font-medium capitalize ${
                            view === v ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'}`}>
                    {v}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-1">
                <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => step(-1)}
                        data-testid="calendar-prev"><ChevronLeft className="h-4 w-4" /></Button>
                <span className="min-w-0 sm:min-w-[150px] text-center text-xs font-semibold text-slate-800"
                      data-testid="calendar-range-label">{label}</span>
                <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => step(1)}
                        data-testid="calendar-next"><ChevronRight className="h-4 w-4" /></Button>
              </div>
              <Button size="sm" variant="outline" className="h-8"
                      onClick={() => setPatternsFor(scopeId || (scopes?.items || [])[0]?.id || '')}
                      data-testid="calendar-patterns-btn">
                <Repeat className="mr-1.5 h-3.5 w-3.5" /> Weekly services
              </Button>
              <Button size="sm" className="h-8" data-testid="calendar-add-btn"
                      onClick={() => setEditing({ date: todayIso, church_id: scopeId })}>
                <Plus className="mr-1.5 h-3.5 w-3.5" /> Add entry
              </Button>
            </div>
          </div>

          {/* Colour key / filter toggles */}
          <div className="mt-3 flex flex-wrap gap-1.5" data-testid="calendar-filters">
            {categories.map((c) => {
              const off = hidden.includes(c.key);
              return (
                <button key={c.key} data-testid={`calendar-filter-${c.key}`}
                        onClick={() => setHidden(off ? hidden.filter((k) => k !== c.key) : [...hidden, c.key])}
                        className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] transition-opacity ${
                          TONE[c.colour].chip} ${off ? 'opacity-35' : ''}`}>
                  <span className={`h-2 w-2 rounded-full ${TONE[c.colour].dot}`} />
                  {c.label}
                  <span className="font-semibold tabular-nums">{data?.counts?.[c.key] ?? 0}</span>
                  {off && <X className="h-3 w-3" />}
                </button>
              );
            })}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-6">
        {error ? (
          <Card><CardContent className="py-12 text-center text-sm text-slate-600"
                              data-testid="calendar-error">{getErrorMessage(error)}</CardContent></Card>
        ) : isLoading ? (
          <Skeleton className="h-[520px] w-full" />
        ) : view === 'agenda' ? (
          <Card><CardContent className="p-0">
            {visible.length === 0 ? (
              <p className="py-16 text-center text-sm text-slate-500" data-testid="calendar-empty">
                Nothing scheduled in this window. Add an entry, or widen the date range.
              </p>
            ) : (
              <div className="divide-y divide-slate-100">
                {visible.map((i) => (
                  <button key={i.id} onClick={() => setSelected(i)}
                          data-testid={`calendar-agenda-${i.id}`}
                          className="flex w-full items-center gap-4 px-4 py-3 text-left hover:bg-slate-50">
                    <span className="w-24 shrink-0 text-xs text-slate-500">
                      {i.date}<br />
                      <span className="tabular-nums text-slate-400">{i.time || 'all day'}</span>
                    </span>
                    <span className={`h-2 w-2 shrink-0 rounded-full ${tone(i.category).dot}`} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-slate-800">{i.title}</span>
                      <span className="block truncate text-[11px] text-slate-500">
                        {i.church_name}{i.location ? ` · ${i.location}` : ''}
                      </span>
                    </span>
                    {i.visibility === 'public' && (
                      <Globe className="h-3.5 w-3.5 shrink-0 text-emerald-600" title="On the public page" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </CardContent></Card>
        ) : (
          <Card><CardContent className="p-3">
            {/* iter 635 — seven columns do not fit a phone. Rather than a
                second mobile calendar, the grid scrolls sideways below 640px
                with a minimum width that keeps a cell legible; from `sm` up it
                is the same full-width grid as before. */}
            <div className="overflow-x-auto">
            <div className="min-w-[640px] sm:min-w-0">
            <div className="mb-1 grid grid-cols-7 gap-1">
              {WEEKDAYS.map((d) => (
                <p key={d} className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">{d}</p>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {(view === 'week' ? weekCells : monthCells).map((day, idx) => (
                <div key={day || `pad-${idx}`} data-testid={day ? `calendar-day-${day}` : undefined}
                     className={`${view === 'week' ? 'min-h-[320px]' : 'min-h-[104px]'} rounded-lg border p-1.5 ${
                       !day ? 'border-transparent' : day === todayIso ? 'border-slate-900 bg-white' : 'border-slate-100 bg-white'}`}>
                  {day && (
                    <>
                      <p className={`px-1 text-[11px] ${day === todayIso ? 'font-bold text-slate-900' : 'text-slate-400'}`}>
                        {Number(day.slice(8))}
                      </p>
                      <div className="space-y-1">
                        {(byDay[day] || []).slice(0, view === 'week' ? 20 : 4).map((i) => (
                          <Chip key={i.id} item={i} />
                        ))}
                        {view === 'month' && (byDay[day] || []).length > 4 && (
                          <button onClick={() => { setView('agenda'); setAnchor(new Date(`${day}T00:00`)); }}
                                  className="px-1 text-[10px] text-slate-400 hover:text-slate-700">
                            +{byDay[day].length - 4} more
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
            </div>
            </div>
          </CardContent></Card>
        )}
      </main>

      {selected && (
        <EntrySummary item={selected} tone={tone(selected.category)}
                      onClose={() => setSelected(null)}
                      onEdit={() => {
                        // Seed from the entry's own first/last day, not the
                        // cell that was clicked — otherwise editing day 3 of a
                        // conference moved the whole run to day 3.
                        setEditing({ ...selected, editingId: selected.entry_id,
                                     date: selected.entry_date || selected.date,
                                     end_date: selected.entry_end_date || '' });
                        setSelected(null);
                      }}
                      onDelete={() => del.mutate(selected.entry_id)} />
      )}
      {editing && (
        <EntryDialog seed={editing} scopes={scopes?.items || []}
                     onClose={() => setEditing(null)}
                     onSaved={() => { setEditing(null); qc.invalidateQueries({ queryKey: ['calendar-feed'] }); }} />
      )}
      {patternsFor !== '' && (
        <PatternsDialog churchId={patternsFor} scopes={scopes?.items || []}
                        onChurchChange={setPatternsFor}
                        onClose={() => { setPatternsFor(''); qc.invalidateQueries({ queryKey: ['calendar-feed'] }); }} />
      )}
    </div>
  );
};

/* ── Entry summary panel ─────────────────────────────────────────── */

const EntrySummary = ({ item, tone, onClose, onEdit, onDelete }) => (
  <Dialog open onOpenChange={(o) => !o && onClose()}>
    <DialogContent data-testid="calendar-entry-summary">
      <DialogHeader>
        <DialogTitle className="flex items-start gap-2">
          <span className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${tone.dot}`} />
          {item.title}
        </DialogTitle>
        <DialogDescription>{item.church_name}</DialogDescription>
      </DialogHeader>
      <div className="space-y-2 text-sm text-slate-700">
        <div className="flex flex-wrap items-center gap-2">
          <Clock className="h-4 w-4 text-slate-400" />
          {item.date}{item.time ? ` at ${item.time}` : ' · all day'}
          {item.ends_at && item.ends_at.slice(0, 10) !== item.date && ` → ${item.ends_at.slice(0, 10)}`}
          {item.recurring && <Badge variant="outline" className="text-[10px]"><Repeat className="mr-1 h-3 w-3" />repeats</Badge>}
        </div>
        {item.location && (
          <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-slate-400" />{item.location}</p>
        )}
        <p className="flex items-center gap-2 text-xs">
          {item.visibility === 'public'
            ? <><Globe className="h-3.5 w-3.5 text-emerald-600" /> Shown on the public church page</>
            : <><Lock className="h-3.5 w-3.5 text-slate-400" /> Internal only — never published</>}
        </p>
        {item.description && <p className="text-sm text-slate-600">{item.description}</p>}
        {(item.lessons || []).length > 0 && (
          <div className="rounded-lg bg-slate-50 p-2" data-testid="calendar-lessons">
            {item.lessons.map((l, i) => (
              <p key={`${l.subject}-${i}`} className="text-xs text-slate-600">
                <span className="mr-2 font-mono text-slate-400">P{l.period}</span>
                {l.subject}{l.class ? ` · ${l.class}` : ''}{l.room ? ` · ${l.room}` : ''}
              </p>
            ))}
          </div>
        )}
        {item.speaker && <p className="text-xs text-slate-500">Speaker: {item.speaker}</p>}
      </div>
      <DialogFooter className="gap-2 sm:justify-between">
        <div className="flex gap-2">
          {item.editable && (
            <>
              <Button size="sm" variant="outline" onClick={onEdit} data-testid="calendar-entry-edit">
                <Pencil className="mr-1 h-3.5 w-3.5" /> Edit
              </Button>
              <Button size="sm" variant="outline" onClick={onDelete}
                      className="text-rose-600 hover:text-rose-700" data-testid="calendar-entry-delete">
                <Trash2 className="mr-1 h-3.5 w-3.5" /> Delete
              </Button>
            </>
          )}
        </div>
        {item.detail_path && !item.editable && (
          <Button asChild size="sm" data-testid="calendar-entry-open">
            <Link to={item.detail_path}>Open full record <ExternalLink className="ml-1 h-3.5 w-3.5" /></Link>
          </Button>
        )}
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

/* ── Create / edit a manual entry ────────────────────────────────── */

const CATS = [['meeting', 'Meeting'], ['service', 'Service'], ['event', 'Event'],
  ['rehearsal', 'Rehearsal'], ['other', 'Other']];

const EntryDialog = ({ seed, scopes, onClose, onSaved }) => {
  const [form, setForm] = useState({
    church_id: seed.church_id || (scopes[0]?.id || ''),
    title: seed.title || '',
    description: seed.description || '',
    category: seed.editingId ? (seed.category || 'meeting') : 'meeting',
    date: seed.date || '',
    end_date: seed.end_date || '',
    start_time: seed.time || '',
    end_time: '',
    location: seed.location || '',
    visibility: seed.visibility || 'internal',
    recurrence: seed.recurrence || 'none',
    repeat_until: seed.repeat_until || '',
  });

  const save = useMutation({
    mutationFn: () => (seed.editingId
      ? api.put(`/calendar/entries/${seed.editingId}`, form)
      : api.post('/calendar/entries', form)),
    onSuccess: () => { toast.success(seed.editingId ? 'Entry updated' : 'Entry added'); onSaved(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // How many days the entry covers, for the hint under the date fields and to
  // keep Save disabled on a reversed range rather than letting the server 422.
  const spanDays = useMemo(() => {
    const from = parseIsoDay(form.date);
    const to = parseIsoDay(form.end_date);
    if (!from || !to) return 1;
    return Math.round((to - from) / 86400000) + 1;
  }, [form.date, form.end_date]);
  const spanInvalid = spanDays < 1;

  const { data: conflicts } = useQuery({
    queryKey: ['calendar-conflicts', form.date, form.church_id, form.start_time, form.end_time],
    queryFn: async () => (await api.get('/calendar/conflicts', {
      params: { date: form.date, church_id: form.church_id, start_time: form.start_time,
                end_time: form.end_time, exclude_entry_id: seed.editingId || '' } })).data,
    enabled: !!form.date,
    retry: false,
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="calendar-entry-dialog">
        <DialogHeader>
          <DialogTitle>{seed.editingId ? 'Edit entry' : 'Add a calendar entry'}</DialogTitle>
          <DialogDescription>
            Meetings, finance reviews, special services — anything with a date.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Title</Label>
            <Input value={form.title} onChange={set('title')} data-testid="entry-title-input"
                   placeholder="Elders' meeting" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Organisation</Label>
              <select value={form.church_id} onChange={set('church_id')} data-testid="entry-church-select"
                      className="h-10 w-full rounded-md border border-slate-200 bg-white px-2 text-sm">
                {scopes.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
            <div><Label>Category</Label>
              <select value={form.category} onChange={set('category')} data-testid="entry-category-select"
                      className="h-10 w-full rounded-md border border-slate-200 bg-white px-2 text-sm">
                {CATS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>First day</Label>
              <Input type="date" value={form.date} onChange={set('date')} data-testid="entry-date-input" />
            </div>
            <div><Label>Last day <span className="text-slate-400">(optional)</span></Label>
              <Input type="date" value={form.end_date} onChange={set('end_date')}
                     min={form.date || undefined} data-testid="entry-end-date-input" />
            </div>
          </div>
          {spanDays > 1 && (
            <p className="text-xs text-slate-500" data-testid="entry-span-hint">
              Runs {spanDays} days, {form.date} to {form.end_date}. It will show on every
              day in between.
            </p>
          )}
          {spanInvalid && (
            <p className="text-xs text-rose-600" data-testid="entry-span-error">
              The last day is before the first day.
            </p>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><Label>From</Label>
              <Input type="time" value={form.start_time} onChange={set('start_time')} data-testid="entry-start-input" />
            </div>
            <div><Label>To</Label>
              <Input type="time" value={form.end_time} onChange={set('end_time')} data-testid="entry-end-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Repeats</Label>
              <select value={form.recurrence} onChange={set('recurrence')} data-testid="entry-recurrence-select"
                      className="h-10 w-full rounded-md border border-slate-200 bg-white px-2 text-sm">
                <option value="none">Does not repeat</option>
                <option value="weekly">Every week</option>
                <option value="monthly">Every month</option>
                <option value="yearly">Every year</option>
              </select>
            </div>
            <div><Label>Until (optional)</Label>
              <Input type="date" value={form.repeat_until} onChange={set('repeat_until')}
                     disabled={form.recurrence === 'none'} data-testid="entry-until-input" />
            </div>
          </div>
          {form.recurrence === 'yearly' && spanDays > 1 && (
            <p className="text-xs text-slate-500" data-testid="entry-yearly-hint">
              Repeats on {form.date?.slice(5)} every year, {spanDays} days each time.
            </p>
          )}

          <div><Label>Location</Label>
            <Input value={form.location} onChange={set('location')} data-testid="entry-location-input" />
          </div>

          {conflicts?.count > 0 && (
            <div className={`rounded-lg border p-3 ${conflicts.time_clashes > 0
              ? 'border-rose-200 bg-rose-50' : 'border-amber-200 bg-amber-50'}`}
                 data-testid="entry-conflict-warning">
              <p className={`flex items-center gap-1.5 text-xs font-semibold ${
                conflicts.time_clashes > 0 ? 'text-rose-800' : 'text-amber-900'}`}>
                <AlertTriangle className="h-3.5 w-3.5" />
                {conflicts.time_clashes > 0
                  ? `${conflicts.time_clashes} clash${conflicts.time_clashes === 1 ? '' : 'es'} at this time`
                  : `${conflicts.count} other thing${conflicts.count === 1 ? '' : 's'} already booked on ${conflicts.weekday}`}
              </p>
              <ul className="mt-1.5 space-y-0.5">
                {conflicts.items.slice(0, 5).map((c) => (
                  <li key={c.id} className="text-[11px] text-slate-600"
                      data-testid={`entry-conflict-${c.id}`}>
                    <span className={`mr-1.5 inline-block h-1.5 w-1.5 rounded-full align-middle ${
                      TONE[(CATEGORY_TONE[c.category]) || 'slate'].dot}`} />
                    <span className="tabular-nums">{c.time || 'all day'}</span> · {c.title}
                    <span className="text-slate-400">{c.church_name ? ` · ${c.church_name}` : ''}</span>
                    {c.time_clash && <span className="ml-1 font-semibold text-rose-700">overlaps</span>}
                  </li>
                ))}
              </ul>
              <p className="mt-1.5 text-[10px] text-slate-500">
                You can still save — this is only a heads-up so branches do not double-book.
              </p>
            </div>
          )}

          <div><Label>Description</Label>
            <Textarea rows={3} value={form.description} onChange={set('description')}
                      data-testid="entry-description-input"
                      placeholder="Shown to visitors when the entry is public." />
          </div>
          <label className="flex cursor-pointer items-start gap-2 rounded-lg bg-slate-50 p-3">
            <input type="checkbox" className="mt-0.5" data-testid="entry-public-checkbox"
                   checked={form.visibility === 'public'}
                   onChange={(e) => setForm({ ...form, visibility: e.target.checked ? 'public' : 'internal' })} />
            <span className="text-xs text-slate-600">
              <span className="font-medium text-slate-800">Show on the public church page</span><br />
              Leave this off for staff meetings, maintenance and finance reviews.
            </span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => save.mutate()}
                  disabled={save.isPending || !form.title.trim() || !form.date
                            || !form.church_id || spanInvalid}
                  data-testid="entry-save-btn">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ── Weekly service patterns ─────────────────────────────────────── */

const PatternsDialog = ({ churchId, scopes, onChurchChange, onClose }) => (
  <Dialog open onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-h-[88vh] overflow-y-auto" data-testid="service-patterns-dialog">
      <DialogHeader>
        <DialogTitle>Weekly services</DialogTitle>
        <DialogDescription>
          Set the pattern once — the calendar projects it forward and public ones appear on the
          church page and its province page. One-off special services are still added as entries.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-3">
        <div><Label>Assembly</Label>
          <select value={churchId} onChange={(e) => onChurchChange(e.target.value)}
                  data-testid="patterns-church-select"
                  className="h-10 w-full rounded-md border border-slate-200 bg-white px-2 text-sm">
            {scopes.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </div>
        <ServiceTimesManager churchId={churchId} />
      </div>
    </DialogContent>
  </Dialog>
);

export default EcosystemCalendarPage;
